// WHY THIS FILE EXISTS:
// `new Date().toISOString().slice(0, 10)` — used all over this app to get
// "today" as YYYY-MM-DD — actually computes the date in UTC, not the
// user's own local calendar day. For any timezone ahead of UTC (India is
// UTC+5:30), that means for the first ~5.5 hours of every local day
// (00:00–05:30 IST), it silently returns YESTERDAY's date instead of
// today's. Any date field with `max={todayISO()}` was therefore blocking
// staff from selecting the actual current date until well into the
// morning — exactly the "today isn't selectable, only yesterday or
// earlier" bug reported on Aug 22, 2026.
//
// These helpers use the browser's own local date components instead,
// which is the correct approach here — unlike the backend (which has to
// hardcode a clinic timezone, since a Docker container's system clock
// often runs in UTC regardless of the clinic's physical location), a
// browser running on staff's own device is already correctly set to
// their real local timezone.

function pad(n) {
  return String(n).padStart(2, '0');
}

// Today's date, in the browser's local timezone, as YYYY-MM-DD. Use this
// anywhere "today" is needed — date-picker max/min values, default form
// values, filtering "today's" records, etc.
export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Formats any Date (or date-parseable value/timestamp) as YYYY-MM-DD in
// the browser's local timezone — for a SPECIFIC moment (e.g. when a
// payment was actually recorded), not just "today". Same UTC pitfall
// applies to a stored timestamp as to "now", so this exists for
// consistency wherever an arbitrary date needs the same correct handling.
export function toLocalISODate(dateValue) {
  const d = new Date(dateValue);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// "N days from now", in the browser's local timezone, as YYYY-MM-DD —
// for "due in the next N days" style comparisons.
export function daysFromNowISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Current year-month, in the browser's local timezone, as YYYY-MM — for
// "this month" style filtering.
export function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}
